import Quill from 'quill';
import Delta from 'quill-delta';

const fontFamilyMap = {
  monospace: 'monospace',
  montserrat: 'montserrat',
  roboto: 'roboto',
  arial: 'arial',
  serif: 'serif',
};

const fontSizeMap = {
  '6px': '6px',
  '8px': '8px',
  '12px': '12px',
  '16px': '16px',
  '18px': '18px',
  '24px': '24px',
};

function extractFontClassFromStyle(
  style: CSSStyleDeclaration
): string | undefined {
  const family = style.fontFamily
    ?.toLowerCase()
    .split(',')[0]
    ?.replace(/['"]/g, '')
    .trim();
  return fontFamilyMap[family] ? fontFamilyMap[family] : undefined;
}

function extractFontSizeFromStyle(
  style: CSSStyleDeclaration
): string | undefined {
  const size = style.fontSize?.toLowerCase();
  return fontSizeMap[size] ? fontSizeMap[size] : undefined;
}

export function htmlToQuillDelta(html: string): Delta {
  const container = document.createElement('div');
  container.innerHTML = html;

  const delta = new Delta();

  const processNode = (
    node: Node,
    inheritedAttrs: any = {},
    listType?: 'bullet' | 'ordered'
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return delta.insert(node.textContent || '', inheritedAttrs);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return delta;

    const el = node as HTMLElement;
    const style = window.getComputedStyle(el);

    let attributes: any = { ...inheritedAttrs };

    // Strong / bold
    if (el.tagName === 'STRONG' || style.fontWeight === 'bold') {
      attributes.bold = true;
    }

    // Em / italic
    if (el.tagName === 'EM' || style.fontStyle === 'italic') {
      attributes.italic = true;
    }

    // Underline
    if (el.tagName === 'U' || style.textDecoration.includes('underline')) {
      attributes.underline = true;
    }

    // Font family
    const font = extractFontClassFromStyle(el.style);
    if (font) attributes.font = font;

    // Font size
    const size = extractFontSizeFromStyle(el.style);
    attributes.size = size || '12px';

    // Color
    if (el.style.color) {
      attributes.color = el.style.color;
    }

    // Background
    if (el.style.backgroundColor) {
      attributes.background = el.style.backgroundColor;
    }

    // Handle lists
    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const fallbackListType = el.tagName === 'UL' ? 'bullet' : 'ordered';

      Array.from(el.children).forEach((child) => {
        if (child.tagName === 'LI') {
          // Use child’s data-list if available, otherwise fallback
          const childListType =
            child.getAttribute('data-list') || fallbackListType;

          // Clone and pass attributes with list type
          const listAttributes = { ...attributes, list: childListType };

          // Process list item content
          delta.insert(child.textContent.trim() + '\n', listAttributes);
        } else {
          // Optionally process non-LI children here
          processNode(child, attributes);
        }
      });

      return delta;
    }

    // List item
    if (el.tagName === 'LI') {
      Array.from(el.childNodes).forEach((child) => {
        processNode(child, attributes);
      });
      delta.insert('\n', { ...attributes, list: listType });
      return delta;
    }

    // Paragraphs and block-level
    if (el.tagName === 'P' || el.tagName === 'DIV') {
      Array.from(el.childNodes).forEach((child) => {
        processNode(child, attributes);
      });
      delta.insert('\n', {});
      return delta;
    }

    // Inline or other
    Array.from(el.childNodes).forEach((child) => {
      processNode(child, attributes);
    });

    return delta;
  };

  Array.from(container.childNodes).forEach((node) => {
    processNode(node);
  });

  return delta;
}
